/**
 * Status de comunicação no escritório virtual.
 * Independente do ponto: não registra jornada, intervalo nem saída.
 */

export const DEFAULT_CLOCK_TZ = "America/Sao_Paulo";
export const PLATFORM_CLOCK_OFFSET = "-03:00";

export const VO_COMM_STATUSES = Object.freeze({
  present: "present",
  focus: "focus",
  lunch: "lunch",
  snack: "snack",
  away: "away",
  in_other_meeting: "in_other_meeting",
});

const SELECTABLE = Object.freeze(["present", "focus", "lunch", "snack", "away"]);
const BUSY = Object.freeze(["focus", "lunch", "snack", "away", "in_other_meeting"]);
const PAUSED = Object.freeze(["focus", "lunch", "snack", "away"]);

export function normalizeVoCommStatus(status) {
  const st = String(status || "present").trim().toLowerCase();
  if (st === "offline") return VO_COMM_STATUSES.away;
  if (Object.values(VO_COMM_STATUSES).includes(st)) return st;
  return VO_COMM_STATUSES.present;
}

export function isVoCommBusyStatus(status) {
  return BUSY.includes(normalizeVoCommStatus(status));
}

export function isVoCommPausedStatus(status) {
  return PAUSED.includes(normalizeVoCommStatus(status));
}

export function isVoCommSelectableStatus(status) {
  return SELECTABLE.includes(normalizeVoCommStatus(status));
}

export function voCommStatusLabel(status) {
  const st = normalizeVoCommStatus(status);
  if (st === "focus") return "Concentrado";
  if (st === "lunch") return "Em horário de almoço";
  if (st === "snack") return "Lanche";
  if (st === "away") return "Ausente";
  if (st === "in_other_meeting") return "Em reunião";
  return "Disponível";
}

export function voCommStatusShortLabel(status) {
  const st = normalizeVoCommStatus(status);
  if (st === "focus") return "concentrado";
  if (st === "lunch") return "almoço";
  if (st === "snack") return "lanche";
  if (st === "away") return "ausente";
  if (st === "in_other_meeting") return "em reunião";
  return "";
}

export function voCommStatusPauseTitle(status) {
  const st = normalizeVoCommStatus(status);
  if (st === "focus") return "Concentrado";
  if (st === "lunch") return "Em horário de almoço";
  if (st === "snack") return "Lanche";
  if (st === "away") return "Ausente";
  return "";
}

export function voCommStatusHint(status, { clockApplies = false } = {}) {
  const st = normalizeVoCommStatus(status);
  if (st === "focus") return "Não perturbe — os colegas veem que você está concentrado.";
  if (st === "lunch") {
    return clockApplies
      ? "Intervalo registrado no ponto. Troque o status para voltar do almoço."
      : "Aviso aos colegas. Não registra jornada nem ponto.";
  }
  if (st === "snack") {
    return clockApplies
      ? "Aviso aos colegas. Não registra intervalo no ponto."
      : "Aviso aos colegas. Não registra jornada nem ponto.";
  }
  if (st === "away") return "Aviso aos colegas. Você continua no escritório, só não está disponível.";
  if (st === "present") return "Disponível para conversar no piso.";
  return "";
}

export function voCommStatusOptions({ clockApplies = false, currentStatus = "" } = {}) {
  const current = normalizeVoCommStatus(currentStatus);
  return SELECTABLE.filter((value) => {
    if (value !== "lunch") return true;
    if (!clockApplies) return true;
    return current === "lunch";
  }).map((value) => ({
    value,
    label:
      value === "focus"
        ? "Concentrado (não perturbe)"
        : value === "lunch"
          ? clockApplies
            ? "Em horário de almoço (ponto)"
            : "Em horário de almoço"
          : value === "snack"
            ? "Lanche"
            : value === "away"
              ? "Ausente"
              : "Disponível",
    hint: voCommStatusHint(value, { clockApplies }),
  }));
}

export function voCommStatusMark(status) {
  const st = normalizeVoCommStatus(status);
  if (st === "focus") return { emoji: "🎯", text: "Foco", title: "Concentrado" };
  if (st === "lunch") return { emoji: "🍽️", text: "Almoço", title: "Em horário de almoço" };
  if (st === "snack") return { emoji: "☕", text: "Lanche", title: "Lanche" };
  if (st === "away") return { emoji: "🌙", text: "Ausente", title: "Ausente" };
  if (st === "in_other_meeting") return { emoji: "📹", text: "Reunião", title: "Em reunião" };
  return null;
}

export function parsePresenceInstant(value) {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const d = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(value || "").trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    const d = new Date(n > 1e12 ? n : n * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = /^\d{4}-\d{2}-\d{2}T/.test(s) ? s : s.replace(" ", "T");
  const withTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? iso : `${iso}${PLATFORM_CLOCK_OFFSET}`;
  const d = new Date(withTz);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatClockTimeInZone(value, timeZone = DEFAULT_CLOCK_TZ) {
  const d = parsePresenceInstant(value);
  if (!d) return "";
  const tz = String(timeZone || DEFAULT_CLOCK_TZ).trim() || DEFAULT_CLOCK_TZ;
  try {
    return d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: tz,
    });
  } catch {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
}

export function formatConnectedDuration(value, now = Date.now()) {
  const d = parsePresenceInstant(value);
  if (!d) return "";
  const minutes = Math.floor(Math.max(0, Number(now) - d.getTime()) / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
}

export function floorJoinCaption({ joinedAt, now = Date.now(), timeZone = DEFAULT_CLOCK_TZ } = {}) {
  const time = formatClockTimeInZone(joinedAt, timeZone);
  const duration = formatConnectedDuration(joinedAt, now);
  if (time && duration) return `Entrou ${time} · ${duration}`;
  if (time) return `Entrou ${time}`;
  return duration;
}

export function resolveClockTimezone(...candidates) {
  for (const raw of candidates) {
    const tz = String(raw || "").trim();
    if (tz) return tz;
  }
  return DEFAULT_CLOCK_TZ;
}
